// components
import { Box, Switch, Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { Icon, IconButton } from '@sdb/planning-ui';
import {
  StyledActionListItemIcon,
  StyledActionListItemText,
  StyledLabelledActionButton,
  StyledListSubheader,
  StyledSecondaryTypography,
  StyledToolbarActionMenuBox,
  StyledToolbarActionMenuPaper
} from './ToolbarActionMenu.styles';

// enums
import { ToolbarPositionEnum } from '../ToolbarWrapper.enums';

// hooks
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsVerticalToolbar } from '../ToolbarPosition.context';

// styles
import {
  StyledBoxFlexAlignCenter,
  StyledRelativeBox
} from 'components/shared/Box/Box.styles';
import { PulsingBadge } from 'components/shared/Badge/PulsingBadge';

// utils
import { getToolbarButtonName } from '../ToolbarSet.utils';

// types
import type { ToolbarActionMenuProps } from './ToolbarActionMenu.types';
import type { ToolbarContextMenuItem } from '../ToolbarSet.types';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

export default function ToolbarActionMenu({
  action,
  additionalActions,
  isAdditionalActions = false,
  setToolbarPosition
}: ToolbarActionMenuProps) {
  const { t } = useTranslation('common', { keyPrefix: 'toolbar' });
  // Popper computes the tooltip placement at runtime, so it cannot come from CSS.
  const isVertical = useIsVerticalToolbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // A vertical toolbar is one icon wide, so a label only fits on a horizontal one.
  const showLabel = !isAdditionalActions && !isVertical && !!action?.label;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultToolbarPositionActions: ToolbarContextMenuItem[] = [
    {
      groupTitle: t('kit'),
      label: t('positionRight'),
      icon: 'arrow-right',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.RIGHT)
    },
    {
      label: t('positionTop'),
      icon: 'arrow-up',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.TOP)
    },
    {
      label: t('zenMode'),
      icon: 'wheat',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.ZEN)
    }
  ];

  const moreActions = additionalActions ? [...additionalActions] : [];
  const menuListActions = isAdditionalActions
    ? [...moreActions, ...defaultToolbarPositionActions]
    : action?.contextMenu;
  const tooltipTitle = isAdditionalActions
    ? t('others', { keyPrefix: '' })
    : action?.tooltipLabel;

  return (
    <>
      <Tooltip title={tooltipTitle} placement={isVertical ? 'left' : 'bottom'}>
        {/* A wrapper, as around a plain toolbar action: it anchors the badge, and a tooltip needs an
            element that still takes pointer events when the button is disabled. The tooltip labels
            this wrapper, not the button, so that label is dropped and the button is named itself. */}
        <StyledRelativeBox aria-label={undefined}>
          {showLabel ? (
            // One outlined button — icon, label and chevron — so the label is plainly this menu's, not the
            // neighbouring action's.
            <StyledLabelledActionButton
              variant="outlined"
              size="small"
              startIcon={
                <Icon type={action?.icon as IconType} fontSize="small" />
              }
              endIcon={<Icon type="chevron-down" fontSize="small" />}
              onClick={handleClick}
              aria-label={action?.ariaLabel}
              aria-controls={open ? action?.id : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              data-tourid={action?.actionDataTourId}
              disabled={action?.disabled}
            >
              {action?.label}
            </StyledLabelledActionButton>
          ) : (
            <IconButton
              type={
                (isAdditionalActions
                  ? 'ellipsis-stroke-vertical'
                  : action?.icon) as IconType
              }
              size="small"
              onClick={handleClick}
              aria-label={getToolbarButtonName(
                action?.ariaLabel ?? tooltipTitle,
                action?.pulsingBadge
              )}
              aria-controls={open ? action?.id : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              data-tourid={action?.actionDataTourId}
              disabled={!isAdditionalActions && action?.disabled}
            />
          )}
          {action?.pulsingBadge?.isActive && (
            <PulsingBadge
              color="primary"
              content={action.pulsingBadge.content ?? ''}
            />
          )}
        </StyledRelativeBox>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id={action?.id}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: StyledToolbarActionMenuPaper
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuList>
          {menuListActions?.map((action, index) => (
            <Box key={index}>
              {action.groupTitle && (
                <StyledListSubheader>{action.groupTitle}</StyledListSubheader>
              )}

              {action.isCheckable ? (
                <StyledToolbarActionMenuBox>
                  <StyledBoxFlexAlignCenter>
                    {action.icon && (
                      <StyledActionListItemIcon>
                        <IconButton
                          type={action.icon}
                          size="small"
                          color="inherit"
                          disabled={action.disabled}
                        />
                      </StyledActionListItemIcon>
                    )}
                    <StyledActionListItemText>
                      {action.label}
                    </StyledActionListItemText>
                  </StyledBoxFlexAlignCenter>
                  <Switch
                    checked={!!action.selected}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick?.();
                    }}
                  />
                </StyledToolbarActionMenuBox>
              ) : (
                <MenuItem
                  onClick={() => {
                    action.onClick?.();
                    handleClose();
                  }}
                  selected={action.selected}
                  // A disabled item cannot be picked, not only its icon greyed out.
                  disabled={action.disabled}
                >
                  {action.icon && (
                    <StyledActionListItemIcon>
                      <IconButton
                        type={action.icon}
                        size="small"
                        color="inherit"
                        disabled={action.disabled}
                      />
                    </StyledActionListItemIcon>
                  )}

                  <ListItemText>{action.label}</ListItemText>

                  {action?.shortcut && (
                    <StyledSecondaryTypography variant="body2">
                      {action?.shortcut}
                    </StyledSecondaryTypography>
                  )}
                </MenuItem>
              )}

              {action.dividerAfter && <Divider />}
            </Box>
          ))}
        </MenuList>
      </Menu>
    </>
  );
}
